import { LandingComponentsConfig, LandingScreenSchema } from '@/modules/Landing/instances/@types/landing-types'
import alertGiftPng from '@/assets/landing/alert-gift.png'
import age18Png from '@/assets/landing-age-selection/lymphatic-walking-main/age-18.png'
import age40Png from '@/assets/landing-age-selection/lymphatic-walking-main/age-40.png'
import age50Png from '@/assets/landing-age-selection/lymphatic-walking-main/age-50.png'
import age60Png from '@/assets/landing-age-selection/lymphatic-walking-main/age-60.png'

/**
 * Printable variant of `age-lymphatic-walking`: same art and same age buckets, plus the printable
 * banner and the quiz note from Figma `g-input-activity` (23768:97067).
 *
 * Structure follows the other printable age pickers (`age-lymphatic-walking-f-pr`,
 * `age-tai-chi-walking-pr`): `LandingAlert` + `LandingTitleExtended` in place of the base
 * selector's `LandingTitle` + `LandingSubtitle` pair.
 */
const schema: LandingScreenSchema = {
	order: ['LandingHead', 'LandingAlert', 'LandingTitleExtended', 'LandingAgeGrid'],

	components: {
		LandingAlert: ({ $t }): LandingComponentsConfig['LandingAlert'] => ({
			text: $t('female_yoga.pages.landing.alert_text'),
			icon: alertGiftPng,
			backgroundColor: 'var(--Fill-Accent-16)',
		}),

		// `LandingTitleExtended` rather than `LandingTitle` + `LandingSubtitle`: its title is
		// 30px / uppercase / 700 — the Figma spec (23768:97082) — and `note` renders the 18px
		// Text-Primary-60 line, replacing the base selector's `female_activity…step_1.subtitle`.
		LandingTitleExtended: ({ $t }): LandingComponentsConfig['LandingTitleExtended'] => ({
			title: $t('main_walking.lymphatic.age_picker.title'),
			note: $t('female_yoga.pages.landing.quiz'),
		}),

		// Four clickable cards, no otherAgesButton — Figma promotes 18-39 out of the escape link and
		// collapses 60-69 + 70+ into one card, whose analyticValue reuses the 60-80 bucket from
		// DEV-34798 so no new analytics value is created.
		// `18_39` and `60_plus_short` are the keys DEV-34965 (PR #673) introduces for this same
		// grid — reused rather than duplicated, and still pending on the localization platform.
		// `60_plus_short` is the bare "60+" the design asks for; `60_plus` renders "Age: 60+".
		LandingAgeGrid: ({ $t }): LandingComponentsConfig['LandingAgeGrid'] => ({
			theme: 'blue',
			ageRanges: [
				{
					value: 18,
					analyticValue: '18-39',
					label: $t('female_yoga.pages.landing.age_range.18_39'),
					img: age18Png,
				},
				{
					value: 40,
					analyticValue: '40-49',
					label: $t('female_yoga.pages.landing.age_range.40_49'),
					img: age40Png,
				},
				{
					value: 50,
					analyticValue: '50-59',
					label: $t('female_yoga.pages.landing.age_range.50_59'),
					img: age50Png,
				},
				{
					value: 60,
					analyticValue: '60-80',
					label: $t('female_yoga.pages.landing.age_range.60_plus_short'),
					img: age60Png,
				},
			],
		}),
	},
}

export default schema
